import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {disabiltyProfileLetterToQualifiers, disabiltyProfileLetterToTitle} from '../../utils'
import {useTranslation} from '@/translations/i18n'
import {EditDetailsSectionButtons} from '@/components/commons/edit-details-section-buttons/EditDetailsSectionButtons'
import {DisabilityProfileIcfCode, DisabilityProfileLetter, ProjectExtended} from '../../types'
import {Dispatch, Fragment, SetStateAction} from 'react'
import {useEditProjectDisabilityProfiles} from '../../queries/useEditProjectDisabilityProfiles'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'
import {DisabilityProfileLetterCardFormSchema} from '../../formSchemas'
import {Controller, useFieldArray, useForm, useWatch} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {errorHandler, raise} from '@/utilities/helpers'
import {StyledGrid, StyledIcfCodeCell} from './style'
import {Text} from '@/components/ui/text/Text'
import {SelectIcfCodes} from '../select-icf-codes/SelectIcfCodes'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {Button} from '@/components/ui/button/Button'
import {Trash01Icon} from '@/components/ui/icon/Icon'
import {useOutletContext} from 'react-router-dom'

export const DisabilityProfileLetterS = ({
    editingLetter,
    setEditingLetter,
    projectId,
    defaultValues
}: {
    editingLetter: DisabilityProfileLetter | null
    setEditingLetter: Dispatch<SetStateAction<DisabilityProfileLetter | null>>
    projectId: string
    defaultValues: DisabilityProfileLetterCardFormSchema['values']
}) => {
    const queryClient = useQueryClient()
    const {t} = useTranslation()
    const {isEditable} = useOutletContext<{isEditable: boolean}>()
    const {control, reset, handleSubmit} = useForm<DisabilityProfileLetterCardFormSchema>({
        resolver: zodResolver(DisabilityProfileLetterCardFormSchema),
        defaultValues: {
            values: defaultValues.concat(
                new Array(5 - defaultValues.length).fill(() => ({
                    icfCode: undefined,
                    qualifier1: undefined,
                    qualifier2: undefined
                }))
            )
        }
    })
    const {fields, remove, append} = useFieldArray({
        control,
        name: 'values'
    })
    const {mutate, isPending} = useEditProjectDisabilityProfiles({
        urlParams: {
            projectId
        },
        options: {
            onSuccess: (_, variables) => {
                queryClient.setQueriesData<ProjectExtended | undefined>(
                    {queryKey: [QUERY_KEYS.PROJECT, projectId]},
                    old => {
                        if (!old) {
                            return old
                        }

                        return {
                            ...old,
                            disabilityProfiles: old.disabilityProfiles
                                .filter(profile => profile.letter != 's')
                                .concat(
                                    variables.profile.map(profile => ({
                                        letter: 's',
                                        icfCode: {code: profile.icfCode},
                                        type: null,
                                        qualifier1: profile.qualifier1,
                                        qualifier2: profile.qualifier2 ?? null
                                    }))
                                )
                        }
                    }
                )
                queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_VALIDATORS, projectId]})
                setEditingLetter(null)
                reset({
                    values: variables.profile
                        .map(profile => ({
                            icfCode: profile.icfCode,
                            qualifier1: profile.qualifier1.toString(),
                            qualifier2: profile.qualifier2?.toString()
                        }))
                        .concat(
                            Array(5 - variables.profile.length).fill({
                                icfCode: undefined,
                                qualifier1: undefined,
                                qualifier2: undefined
                            })
                        )
                })
            },
            onError: errorHandler
        }
    })

    const onSubmit = handleSubmit((data: DisabilityProfileLetterCardFormSchema) => {
        mutate({
            letter: 's',
            profile: data.values
                .filter(value => value.icfCode)
                .map(value => ({
                    icfCode: value.icfCode ?? raise('icfCode is required'),
                    qualifier1: Number(value.qualifier1),
                    qualifier2: Number(value.qualifier2),
                    type: null,
                    letter: 's'
                }))
        })
    })

    const valuesWatch = useWatch({control, name: 'values'})

    return (
        <DetailsFieldsSection
            variant="neutral"
            title={t(disabiltyProfileLetterToTitle.s)}
            isDisabled={!!editingLetter && editingLetter != 's'}
            headingEndSlot={
                isEditable && (
                    <EditDetailsSectionButtons
                        isDisabled={isPending}
                        isLoading={isPending}
                        isActive={editingLetter == 's'}
                        onConfirm={onSubmit}
                        onCancel={() => {
                            setEditingLetter(null)
                            reset()
                        }}
                        onEdit={() => setEditingLetter('s')}
                    />
                )
            }
        >
            <StyledGrid>
                <Text palette="neutral" shade="500" size="textXs">
                    {t('projects:icf_code')}
                </Text>
                <Text palette="neutral" shade="500" size="textXs">
                    {t('projects:extent_of_impairment')}
                </Text>
                <Text palette="neutral" shade="500" size="textXs">
                    {t('projects:nature_of_change')}
                </Text>
                <div />
                {fields.map((field, rowIndex) => (
                    <Fragment key={field.id}>
                        <StyledIcfCodeCell>
                            <Text size="textSm" weight="500">
                                {rowIndex + 1}.
                            </Text>
                            {editingLetter == 's' ? (
                                <Controller
                                    control={control}
                                    name={`values.${rowIndex}.icfCode`}
                                    render={({field, fieldState}) => (
                                        <SelectIcfCodes
                                            exludedOptions={
                                                valuesWatch
                                                    .map(value => value.icfCode)
                                                    .filter(Boolean) as DisabilityProfileIcfCode[]
                                            }
                                            disabled={
                                                rowIndex >= 1 &&
                                                (!valuesWatch[rowIndex - 1]?.icfCode ||
                                                    !valuesWatch[rowIndex - 1]?.qualifier1 ||
                                                    !valuesWatch[rowIndex - 1]?.qualifier2)
                                            }
                                            letter={'s'}
                                            value={field.value}
                                            onChange={field.onChange}
                                            errorMessage={fieldState.error?.message}
                                        />
                                    )}
                                />
                            ) : (
                                <Text size="textSm" palette="neutral" shade="600">
                                    {field?.icfCode ?? '-'}
                                </Text>
                            )}
                        </StyledIcfCodeCell>
                        {editingLetter == 's' ? (
                            <Controller
                                control={control}
                                name={`values.${rowIndex}.qualifier1`}
                                render={({field, fieldState}) => (
                                    <InputSelect
                                        disabled={!valuesWatch[rowIndex]?.icfCode}
                                        sameWidth={false}
                                        displayValue={() => field.value}
                                        optionsGroups={[
                                            {
                                                label: disabiltyProfileLetterToQualifiers.s[0].label,
                                                options: disabiltyProfileLetterToQualifiers.s[0].options.map(
                                                    option =>
                                                        [
                                                            option.value.toString(),
                                                            `${option.value} - ${t(option.label)}`
                                                        ] as [string, string]
                                                )
                                            }
                                        ]}
                                        value={field.value}
                                        onChange={field.onChange}
                                        errorMessage={fieldState.error?.message}
                                    />
                                )}
                            />
                        ) : (
                            <Text size="textSm" palette="neutral" shade="600">
                                {valuesWatch[rowIndex]?.qualifier1 ?? '-'}
                            </Text>
                        )}
                        {editingLetter == 's' ? (
                            <Controller
                                control={control}
                                name={`values.${rowIndex}.qualifier2`}
                                render={({field, fieldState}) => (
                                    <InputSelect
                                        disabled={!valuesWatch[rowIndex]?.qualifier1}
                                        sameWidth={false}
                                        displayValue={() => field.value}
                                        optionsGroups={[
                                            {
                                                label: disabiltyProfileLetterToQualifiers.s[1].label,
                                                options: disabiltyProfileLetterToQualifiers.s[1].options.map(
                                                    option =>
                                                        [
                                                            option.value.toString(),
                                                            `${option.value} - ${t(option.label)}`
                                                        ] as [string, string]
                                                )
                                            }
                                        ]}
                                        value={field.value}
                                        onChange={field.onChange}
                                        errorMessage={fieldState.error?.message}
                                    />
                                )}
                            />
                        ) : (
                            <Text size="textSm" palette="neutral" shade="600">
                                {valuesWatch[rowIndex]?.qualifier2 ?? '-'}
                            </Text>
                        )}
                        {editingLetter == 's' && valuesWatch[rowIndex]?.icfCode ? (
                            <Button
                                variant="ghost"
                                shape="square"
                                onClick={() => {
                                    remove(rowIndex)
                                    append({
                                        icfCode: undefined,
                                        qualifier1: undefined,
                                        qualifier2: undefined
                                    })
                                }}
                            >
                                <Trash01Icon />
                            </Button>
                        ) : (
                            <div />
                        )}
                    </Fragment>
                ))}
            </StyledGrid>
        </DetailsFieldsSection>
    )
}
